import React, { useState, useEffect } from 'react';
import CRUDList from '../Common/CRUDList';
import DateInput from '../Common/DateInput';
import TimeInput from '../Common/TimeInput';
import { legsSchedule, legs, routes, airports, planes } from '../../services/api';
import { formatDateTime, formatDate, formatFlightTimeRange } from '../../utils/dateFormat';
import { legsScheduleStatuses, legsScheduleStatusColors } from '../../constants/travelEnums';

function LegsScheduleForm({ formData, setFormData, onValidationChange }) {
  const [legsList, setLegsList] = useState([]);
  const [routesList, setRoutesList] = useState([]);
  const [scheduleList, setScheduleList] = useState([]);
  const [airportsList, setAirportsList] = useState([]);
  const [planesList, setPlanesList] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const isReplacement = formData.lsch_init_id != null;

  useEffect(() => {
    loadLegs();
    loadRoutes();
    loadSchedule();
    loadAirports();
    loadPlanes();
  }, []);

  // Auto-calculate offset when leg is selected and date/time changes
  useEffect(() => {
    if (formData.lsch_leg_id && formData.lsch_dep_date && formData.lsch_dep_time && legsList.length > 0 && routesList.length > 0 && airportsList.length > 0) {
      const leg = legsList.find(l => l.leg_id === formData.lsch_leg_id);
      if (!leg) return;

      const route = routesList.find(r => r.route_id === leg.leg_route_id);
      if (!route) return;

      const depAirport = airportsList.find(a => a.airport_iata_code === (route.route_direction === 1 ? route.airport1_iata : route.airport2_iata));
      const arrAirport = airportsList.find(a => a.airport_iata_code === (route.route_direction === 1 ? route.airport2_iata : route.airport1_iata));

      if (depAirport?.tz_code && !formData.lsch_dep_offset) {
        const depOffset = getTimezoneOffsetMinutes(new Date(`${formData.lsch_dep_date}T${formData.lsch_dep_time}:00`), depAirport.tz_code);
        setFormData(prev => ({ ...prev, lsch_dep_offset: depOffset }));
      }

      if (arrAirport?.tz_code && formData.lsch_arr_date && formData.lsch_arr_time && !formData.lsch_arr_offset) {
        const arrOffset = getTimezoneOffsetMinutes(new Date(`${formData.lsch_arr_date}T${formData.lsch_arr_time}:00`), arrAirport.tz_code);
        setFormData(prev => ({ ...prev, lsch_arr_offset: arrOffset }));
      }
    }
  }, [formData.lsch_leg_id, formData.lsch_dep_date, formData.lsch_dep_time, formData.lsch_arr_date, formData.lsch_arr_time, legsList, routesList, airportsList]);

  const getTimezoneOffsetMinutes = (date, timezone) => {
    try {
      // For dates outside reasonable range (before 1970 or after 2100),
      // use current date to determine approximate offset
      let dateToUse = date;
      const year = date.getFullYear();
      if (year < 1970 || year > 2100 || isNaN(year)) {
        // Use equivalent date in current year to get reasonable offset
        const now = new Date();
        dateToUse = new Date(now.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes());
      }

      // Create a date string in the target timezone and parse the offset
      const dateInTz = new Date(dateToUse.toLocaleString('en-US', { timeZone: timezone }));
      const dateInUtc = new Date(dateToUse.toLocaleString('en-US', { timeZone: 'UTC' }));

      // The difference tells us the offset
      const offsetMs = dateInTz.getTime() - dateInUtc.getTime();
      return Math.round(offsetMs / 60000);
    } catch (err) {
      console.error('Failed to get timezone offset', err);
      return 0;
    }
  };

  const loadLegs = async () => {
    try {
      const response = await legs.getAll();
      setLegsList(response.data.filter(l => !l.leg_operational_leg_id));
    } catch (err) {
      console.error('Failed to load legs', err);
    }
  };

  const loadRoutes = async () => {
    try {
      const response = await routes.getAll();
      setRoutesList(response.data);
    } catch (err) {
      console.error('Failed to load routes', err);
    }
  };

  const loadPlanes = async () => {
    try {
      const response = await planes.getAll();
      setPlanesList(response.data);
    } catch (err) {
      console.error('Failed to load planes', err);
    }
  };

  const loadSchedule = async () => {
    try {
      const response = await legsSchedule.getAll();
      setScheduleList(response.data);
    } catch (err) {
      console.error('Failed to load schedule', err);
    }
  };

  const loadAirports = async () => {
    try {
      const response = await airports.getAll();
      setAirportsList(response.data);
    } catch (err) {
      console.error('Failed to load airports', err);
    }
  };

  const formatOffsetDisplay = (offset) => {
    if (offset === null || offset === undefined || offset === '') return '';
    const sign = offset >= 0 ? '+' : '-';
    const absOffset = Math.abs(offset);
    const hours = Math.floor(absOffset / 60);
    const minutes = absOffset % 60;
    return `UTC${sign}${hours}${minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : ''}`;
  };

  // Helper function to copy scheduled departure time to actual
  const copyScheduledDepartureToActual = () => {
    setFormData((prev) => ({
      ...prev,
      actual_dep_date: prev.lsch_dep_date,
      actual_dep_time: prev.lsch_dep_time,
      actual_dep_offset: prev.lsch_dep_offset
    }));
  };

  // Helper function to copy scheduled arrival time to actual
  const copyScheduledArrivalToActual = () => {
    setFormData((prev) => ({
      ...prev,
      actual_arr_date: prev.lsch_arr_date,
      actual_arr_time: prev.lsch_arr_time,
      actual_arr_offset: prev.lsch_arr_offset
    }));
  };

  // Helper to get airports by IATA for actual route
  const getAirportsForRoute = (routeId) => {
    const route = routesList.find(r => r.route_id === routeId);
    if (!route) return { dep: null, arr: null };
    const dep = airportsList.find(a => a.airport_iata_code === route.airport1_iata);
    const arr = airportsList.find(a => a.airport_iata_code === route.airport2_iata);
    return route.route_direction === 1
      ? { dep, arr }
      : { dep: arr, arr: dep };
  };

  // Auto-calc actual offsets when status is completed and offsets are missing
  useEffect(() => {
    if (formData.lsch_status !== 40) return;
    const legRouteId = legsList.find(l => l.leg_id === formData.lsch_leg_id)?.leg_route_id || null;
    const routeId = formData.actual_route_id || formData.lflight_route_id || legRouteId || null;
    const { dep, arr } = routeId ? getAirportsForRoute(routeId) : { dep: null, arr: null };

    if (dep?.tz_code && formData.actual_dep_date && formData.actual_dep_time && (formData.actual_dep_offset === null || formData.actual_dep_offset === undefined || formData.actual_dep_offset === '')) {
      const offset = getTimezoneOffsetMinutes(new Date(`${formData.actual_dep_date}T${formData.actual_dep_time}:00`), dep.tz_code);
      setFormData(prev => ({ ...prev, actual_dep_offset: offset }));
    }

    if (arr?.tz_code && formData.actual_arr_date && formData.actual_arr_time && (formData.actual_arr_offset === null || formData.actual_arr_offset === undefined || formData.actual_arr_offset === '')) {
      const offset = getTimezoneOffsetMinutes(new Date(`${formData.actual_arr_date}T${formData.actual_arr_time}:00`), arr.tz_code);
      setFormData(prev => ({ ...prev, actual_arr_offset: offset }));
    }
  }, [formData.lsch_status, formData.actual_route_id, formData.actual_dep_date, formData.actual_dep_time, formData.actual_dep_offset, formData.actual_arr_date, formData.actual_arr_time, formData.actual_arr_offset, formData.lflight_route_id, formData.lsch_leg_id, routesList, airportsList, legsList]);

  // Validate times on every change
  useEffect(() => {
    const errors = {};

    // Check scheduled times (planned departure/arrival)
    if (formData.lsch_dep_date && formData.lsch_dep_time && formData.lsch_arr_date && formData.lsch_arr_time) {
      const depDt = new Date(`${formData.lsch_dep_date}T${formData.lsch_dep_time}:00Z`);
      const arrDt = new Date(`${formData.lsch_arr_date}T${formData.lsch_arr_time}:00Z`);
      // Convert to UTC by accounting for timezones
      const depUtcMin = Math.floor(depDt.getTime() / 60000) - (formData.lsch_dep_offset || 0);
      const arrUtcMin = Math.floor(arrDt.getTime() / 60000) - (formData.lsch_arr_offset || 0);
      if (arrUtcMin <= depUtcMin) {
        errors.scheduledTimes = 'Scheduled arrival time must be after departure time';
      }
    }

    // Check actual times (if status is Completed and BOTH times are fully provided)
    if (formData.lsch_status === 40) {
      const hasCompleteDep = formData.actual_dep_date && formData.actual_dep_time && (formData.actual_dep_offset !== null && formData.actual_dep_offset !== undefined && formData.actual_dep_offset !== '');
      const hasCompleteArr = formData.actual_arr_date && formData.actual_arr_time && (formData.actual_arr_offset !== null && formData.actual_arr_offset !== undefined && formData.actual_arr_offset !== '');
      
      if (hasCompleteDep && hasCompleteArr) {
        const depDt = new Date(`${formData.actual_dep_date}T${formData.actual_dep_time}:00Z`);
        const arrDt = new Date(`${formData.actual_arr_date}T${formData.actual_arr_time}:00Z`);
        // Convert to UTC by accounting for timezones
        const depUtcMin = Math.floor(depDt.getTime() / 60000) - (formData.actual_dep_offset || 0);
        const arrUtcMin = Math.floor(arrDt.getTime() / 60000) - (formData.actual_arr_offset || 0);
        if (arrUtcMin <= depUtcMin) {
          errors.actualTimes = 'Actual arrival time must be after departure time';
        }
      }
    }

    setValidationErrors(errors);
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0);
    }
  }, [formData.lsch_dep_date, formData.lsch_dep_time, formData.lsch_arr_date, formData.lsch_arr_time, formData.actual_dep_date, formData.actual_dep_time, formData.actual_dep_offset, formData.actual_arr_date, formData.actual_arr_time, formData.actual_arr_offset, formData.lsch_status, onValidationChange]);

  return (
    <>
      {!formData.lsch_id && (
        <div className="form-group">
          <label>Original Schedule Entry (for replacement)</label>
          <select
            value={formData.replaced_lsch_id || ''}
            onChange={(e) => {
              const replacedId = e.target.value ? parseInt(e.target.value) : null;
              if (replacedId) {
                const replacedEntry = scheduleList.find(ls => ls.lsch_id === replacedId);
                const initId = replacedEntry?.lsch_init_id || replacedId;
                const chainEntries = scheduleList.filter(ls =>
                  ls.lsch_init_id === initId || ls.lsch_id === initId
                );
                const maxChgNum = Math.max(...chainEntries.map(ls => ls.lsch_chg_num || 0));
                setFormData({
                  ...formData,
                  replaced_lsch_id: replacedId,
                  lsch_init_id: initId,
                  lsch_chg_num: maxChgNum + 1,
                  lsch_leg_id: replacedEntry?.lsch_leg_id || formData.lsch_leg_id
                });
              } else {
                setFormData({ ...formData, replaced_lsch_id: null, lsch_init_id: null, lsch_chg_num: 0 });
              }
            }}
          >
            <option value="">None (original entry)</option>
            {scheduleList
              .filter(ls => ls.lsch_status === 10)
              .map(ls => (
                <option key={ls.lsch_id} value={ls.lsch_id}>
                  {ls.carrier_iata || ls.carrier_name} {ls.flight_number}: {ls.dep_iata} → {ls.arr_iata} (Dep: {formatDateTime(ls.lsch_dep_date, ls.lsch_dep_time)})
                </option>
              ))}
          </select>
          {isReplacement && (
            <small className="text-muted">Change #: {formData.lsch_chg_num}</small>
          )}
        </div>
      )}

      <div className="form-group">
        <label>Schedule Date *</label>
        <DateInput
          value={formData.lsch_date || ''}
          onChange={(value) => setFormData({ ...formData, lsch_date: value })}
          required
        />
      </div>

      <div className="form-group">
        <label>Leg *</label>
        <select
          value={formData.lsch_leg_id || ''}
          onChange={(e) => setFormData({ ...formData, lsch_leg_id: parseInt(e.target.value) })}
          required
        >
          <option value="">Select leg</option>
          {legsList.map(leg => (
            <option key={leg.leg_id} value={leg.leg_id}>
              {leg.carrier_iata || leg.carrier_name} {leg.flight_number}: {leg.dep_iata} → {leg.arr_iata}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '10px' }}>
        <div className="form-group">
          <label>Departure Date *</label>
          <DateInput
            value={formData.lsch_dep_date || ''}
            onChange={(value) => setFormData({ ...formData, lsch_dep_date: value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Departure Time *</label>
          <TimeInput
            value={formData.lsch_dep_time || ''}
            onChange={(value) => setFormData({ ...formData, lsch_dep_time: value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Offset (min)</label>
          <input
            type="number"
            value={formData.lsch_dep_offset ?? ''}
            onChange={(e) => setFormData({ ...formData, lsch_dep_offset: e.target.value ? parseInt(e.target.value) : 0 })}
            placeholder="0"
          />
          <small style={{ color: '#7f8c8d' }}>{formatOffsetDisplay(formData.lsch_dep_offset)}</small>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '10px' }}>
        <div className="form-group">
          <label>Arrival Date *</label>
          <DateInput
            value={formData.lsch_arr_date || ''}
            onChange={(value) => setFormData({ ...formData, lsch_arr_date: value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Arrival Time *</label>
          <TimeInput
            value={formData.lsch_arr_time || ''}
            onChange={(value) => setFormData({ ...formData, lsch_arr_time: value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Offset (min)</label>
          <input
            type="number"
            value={formData.lsch_arr_offset ?? ''}
            onChange={(e) => setFormData({ ...formData, lsch_arr_offset: e.target.value ? parseInt(e.target.value) : 0 })}
            placeholder="0"
          />
          <small style={{ color: '#7f8c8d' }}>{formatOffsetDisplay(formData.lsch_arr_offset)}</small>
        </div>
      </div>

      {validationErrors.scheduledTimes && (
        <div style={{ color: '#e74c3c', padding: '10px', backgroundColor: '#fadbd8', borderRadius: '4px', marginBottom: '10px' }}>
          {validationErrors.scheduledTimes}
        </div>
      )}

      <div className="form-group">
        <label>Status *</label>
        <select
          value={formData.lsch_status ?? ''}
          onChange={(e) => setFormData({ ...formData, lsch_status: parseInt(e.target.value) })}
          required
        >
          <option value="">Select status</option>
          {Object.entries(legsScheduleStatuses).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Change Date</label>
        <DateInput
          value={formData.lsch_change_date || ''}
          onChange={(value) => setFormData({ ...formData, lsch_change_date: value })}
        />
        <small className="text-muted">For cancelled or changed entries</small>
      </div>

      {formData.lsch_status === 40 && (
        <div className="form-section" style={{ marginTop: '12px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
          <h4 style={{ marginTop: 0, marginBottom: '8px' }}>Actual Flight Data</h4>

          <div className="form-group">
            <label>Actual Route</label>
            <select
              value={formData.actual_route_id || ''}
              onChange={(e) => setFormData({ ...formData, actual_route_id: e.target.value ? parseInt(e.target.value) : null })}
            >
              <option value="">Use scheduled route</option>
              {routesList.map(r => (
                <option key={r.route_id} value={r.route_id}>
                  {r.dep_iata} → {r.arr_iata} ({r.route_direction === 1 ? 'dir1' : 'dir2'})
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <label style={{ fontWeight: 'bold', marginBottom: 0 }}>Actual Departure</label>
            <button
              type="button"
              onClick={copyScheduledDepartureToActual}
              style={{
                padding: '4px 12px',
                fontSize: '0.85em',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title="Copy scheduled departure time to actual"
            >
              Copy from Scheduled
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '10px' }}>
            <div className="form-group">
              <label>Actual Departure Date</label>
              <DateInput
                value={formData.actual_dep_date || ''}
                onChange={(value) => setFormData({ ...formData, actual_dep_date: value, actual_dep_offset: value ? formData.actual_dep_offset : null })}
              />
            </div>
            <div className="form-group">
              <label>Actual Departure Time</label>
              <TimeInput
                value={formData.actual_dep_time || ''}
                onChange={(value) => setFormData({ ...formData, actual_dep_time: value, actual_dep_offset: value ? formData.actual_dep_offset : null })}
              />
            </div>
            <div className="form-group">
              <label>Dep Offset (min)</label>
              <input
                type="number"
                value={formData.actual_dep_offset ?? ''}
                onChange={(e) => setFormData({ ...formData, actual_dep_offset: e.target.value ? parseInt(e.target.value) : null })}
              />
              <small style={{ color: '#7f8c8d' }}>{formatOffsetDisplay(formData.actual_dep_offset)}</small>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', marginTop: '10px' }}>
            <label style={{ fontWeight: 'bold', marginBottom: 0 }}>Actual Arrival</label>
            <button
              type="button"
              onClick={copyScheduledArrivalToActual}
              style={{
                padding: '4px 12px',
                fontSize: '0.85em',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title="Copy scheduled arrival time to actual"
            >
              Copy from Scheduled
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '10px' }}>
            <div className="form-group">
              <label>Actual Arrival Date</label>
              <DateInput
                value={formData.actual_arr_date || ''}
                onChange={(value) => setFormData({ ...formData, actual_arr_date: value, actual_arr_offset: value ? formData.actual_arr_offset : null })}
              />
            </div>
            <div className="form-group">
              <label>Actual Arrival Time</label>
              <TimeInput
                value={formData.actual_arr_time || ''}
                onChange={(value) => setFormData({ ...formData, actual_arr_time: value, actual_arr_offset: value ? formData.actual_arr_offset : null })}
              />
            </div>
            <div className="form-group">
              <label>Arr Offset (min)</label>
              <input
                type="number"
                value={formData.actual_arr_offset ?? ''}
                onChange={(e) => setFormData({ ...formData, actual_arr_offset: e.target.value ? parseInt(e.target.value) : null })}
              />
              <small style={{ color: '#7f8c8d' }}>{formatOffsetDisplay(formData.actual_arr_offset)}</small>
            </div>
          </div>

          {validationErrors.actualTimes && (
            <div style={{ color: '#e74c3c', padding: '10px', backgroundColor: '#fadbd8', borderRadius: '4px', marginBottom: '10px' }}>
              {validationErrors.actualTimes}
            </div>
          )}          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div className="form-group">
              <label>Distance (meters)</label>
              <input
                type="number"
                value={formData.actual_distance ?? ''}
                onChange={(e) => setFormData({ ...formData, actual_distance: e.target.value ? parseInt(e.target.value) : null })}
                min={0}
              />
            </div>
            <div className="form-group">
              <label>Aircraft</label>
              <select
                value={formData.actual_plane_id || ''}
                onChange={(e) => setFormData({ ...formData, actual_plane_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Not specified</option>
                {planesList.map(p => (
                  <option key={p.plane_id} value={p.plane_id}>
                    {p.plane_msn} ({p.plane_id})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function LegsScheduleList() {
  const [statusFilter, setStatusFilter] = useState(10); // Default to "Scheduled" (10)
  const [formValidationErrors, setFormValidationErrors] = useState({});

  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    return `${hours}:${pad(mins)}`;
  };

  const formatDelay = (minutes) => {
    if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return null;
    if (minutes === 0) return { text: 'on time', color: '#7f8c8d' };
    const sign = minutes > 0 ? '+' : '−';
    const abs = Math.abs(minutes);
    return {
      text: `${sign}${abs}m`,
      color: minutes > 0 ? '#c0392b' : '#27ae60'
    };
  };

  const formatDistance = (meters) => {
    if (meters === null || meters === undefined) return '-';
    const km = Math.round(meters / 1000);
    return `${km.toLocaleString('en-US').replace(/,/g, ' ')} km`;
  };

  const formatDeltaValue = (value, { positiveIsGood }) => {
    if (value === null || value === undefined || Number.isNaN(value)) return null;
    if (value === 0) return { text: '±0', color: '#7f8c8d' };
    const sign = value > 0 ? '+' : '−';
    const abs = Math.abs(value);
    const color = value > 0
      ? (positiveIsGood ? '#27ae60' : '#c0392b')
      : (positiveIsGood ? '#c0392b' : '#27ae60');
    return { text: `${sign}${abs}`, color };
  };

  const sortByDeparture = (a, b) => {
    const toNumber = (value) => (value === null || value === undefined ? 0 : value);
    const asc = toNumber(a.lsch_dep_utc_min) - toNumber(b.lsch_dep_utc_min);
    const desc = toNumber(b.lsch_dep_utc_min) - toNumber(a.lsch_dep_utc_min);
    return statusFilter === 10 ? asc : desc;
  };

  const filterComponent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <label style={{ fontWeight: 500, marginRight: '5px' }}>Filter:</label>
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value ? parseInt(e.target.value) : null)}
        style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
      >
        <option value="">All</option>
        {Object.entries(legsScheduleStatuses).map(([key, value]) => (
          <option key={key} value={key}>{value}</option>
        ))}
      </select>
    </div>
  );

  return (
    <CRUDList
      title="Legs Schedule"
      api={legsSchedule}
      columns={['ID', 'Flight', 'Route', 'Departure', 'Arrival', 'Duration', 'Distance', 'Plane', 'Status']}
      FormComponent={LegsScheduleForm}
      formProps={{ onValidationChange: setFormValidationErrors }}
      filterComponent={filterComponent}
      sortFn={sortByDeparture}
      isFormValid={formValidationErrors}
      defaultFormData={{
        lsch_date: new Date().toISOString().split('T')[0],
        lsch_init_id: null,
        lsch_chg_num: 0,
        lsch_leg_id: '',
        lsch_dep_date: '',
        lsch_dep_time: '',
        lsch_dep_offset: 0,
        lsch_arr_date: '',
        lsch_arr_time: '',
        lsch_arr_offset: 0,
        lsch_status: 10,
        lsch_change_date: '',
        lsch_lflight_id: null,
        lflight_route_id: null,
        lflight_dep_date: '',
        lflight_dep_time: '',
        lflight_dep_offset: null,
        lflight_arr_date: '',
        lflight_arr_time: '',
        lflight_arr_offset: null,
        lflight_plane_id: null,
        actual_route_id: null,  // Default to NULL (scheduled route)
        actual_dep_date: '',
          actual_dep_time: '',
          actual_dep_offset: null,
          actual_arr_date: '',
          actual_arr_time: '',
          actual_arr_offset: null,
          actual_distance: null,
          actual_plane_id: null
        }}
        useOldStyle={true}
        filterFn={(item) => statusFilter === null || statusFilter === '' ? true : item.lsch_status === statusFilter}
        renderRow={(item) => (
          <>
            <td>{item.lsch_id}</td>
            <td>
              <strong>{item.carrier_iata || item.carrier_name} {item.flight_number}</strong>
              {item.lsch_chg_num > 0 && (
                <span style={{ marginLeft: '5px', fontSize: '0.8em', color: '#7f8c8d' }}>
                  (chg #{item.lsch_chg_num})
                </span>
              )}
            </td>
            <td>
              {(() => {
                const isCompleted = item.lsch_status === 40;
                const hasActualRoute = isCompleted && item.lflight_dep_iata && item.lflight_arr_iata;
                const routeMismatch = hasActualRoute && 
                  (item.dep_iata !== item.lflight_dep_iata || item.arr_iata !== item.lflight_arr_iata);
                
                if (routeMismatch) {
                  return (
                    <div>
                      <div style={{ textDecoration: 'line-through', color: '#7f8c8d' }}>
                        {item.dep_iata} → {item.arr_iata}
                      </div>
                      <div>
                        {item.lflight_dep_iata} → {item.lflight_arr_iata}
                      </div>
                    </div>
                  );
                }
                return `${item.dep_iata} → ${item.arr_iata}`;
              })()}
            </td>
            <td>
              {(() => {
                // Always show scheduled departure time
                return formatDateTime(item.lsch_dep_date, item.lsch_dep_time);
              })()}
              {(() => {
                const isCompleted = item.lsch_status === 40;
                const schedLocal = item.lsch_dep_utc_min != null && item.lsch_dep_offset != null
                  ? item.lsch_dep_utc_min + item.lsch_dep_offset
                  : null;
                const actualLocal = item.lflight_dep_utc_min != null && item.lflight_dep_offset != null
                  ? item.lflight_dep_utc_min + item.lflight_dep_offset
                  : null;
                const delay = isCompleted && schedLocal != null && actualLocal != null
                  ? actualLocal - schedLocal
                  : null;
                const info = formatDelay(delay);
                
                if (!info) return null;
                
                // Get actual time for tooltip
                const actualMinutes = item.lflight_dep_utc_min + item.lflight_dep_offset;
                const depDateObj = new Date(actualMinutes * 60000);
                const depDateStr = depDateObj.toISOString().split('T')[0];
                const depTimeStr = `${String(depDateObj.getUTCHours()).padStart(2, '0')}:${String(depDateObj.getUTCMinutes()).padStart(2, '0')}`;
                const actualTimeFormatted = formatDateTime(depDateStr, depTimeStr);
                
                return (
                  <span 
                    style={{ fontSize: '0.85em', marginLeft: '6px', color: info.color, cursor: 'help' }}
                    title={`Actual: ${actualTimeFormatted}`}
                  >
                    {info.text}
                  </span>
                );
              })()}
            </td>
            <td>
              {(() => {
                // Always show scheduled arrival time
                return formatDateTime(item.lsch_arr_date, item.lsch_arr_time);
              })()}
              {(() => {
                const isCompleted = item.lsch_status === 40;
                const schedLocal = item.lsch_arr_utc_min != null && item.lsch_arr_offset != null
                  ? item.lsch_arr_utc_min + item.lsch_arr_offset
                  : null;
                const actualLocal = item.lflight_arr_utc_min != null && item.lflight_arr_offset != null
                  ? item.lflight_arr_utc_min + item.lflight_arr_offset
                  : null;
                const delay = isCompleted && schedLocal != null && actualLocal != null
                  ? actualLocal - schedLocal
                  : null;
                const info = formatDelay(delay);
                
                if (!info) return null;
                
                // Get actual time for tooltip
                const actualMinutes = item.lflight_arr_utc_min + item.lflight_arr_offset;
                const arrDateObj = new Date(actualMinutes * 60000);
                const arrDateStr = arrDateObj.toISOString().split('T')[0];
                const arrTimeStr = `${String(arrDateObj.getUTCHours()).padStart(2, '0')}:${String(arrDateObj.getUTCMinutes()).padStart(2, '0')}`;
                const actualTimeFormatted = formatDateTime(arrDateStr, arrTimeStr);
                
                return (
                  <span 
                    style={{ fontSize: '0.85em', marginLeft: '6px', color: info.color, cursor: 'help' }}
                    title={`Actual: ${actualTimeFormatted}`}
                  >
                    {info.text}
                  </span>
                );
              })()}
            </td>
            <td>
              {(() => {
                // Always show scheduled duration
                if (item.lsch_duration != null && item.lsch_duration >= 0) {
                  return formatDuration(item.lsch_duration);
                } else {
                  return '-';
                }
              })()}
              {(() => {
                const isCompleted = item.lsch_status === 40;
                const hasActualTimes = item.lflight_arr_utc_min != null && item.lflight_dep_utc_min != null;
                const actualDuration = hasActualTimes ? item.lflight_arr_utc_min - item.lflight_dep_utc_min : null;
                const delta = isCompleted && actualDuration != null && item.lsch_duration != null
                  ? actualDuration - item.lsch_duration
                  : null;
                const info = formatDelay(delta);
                
                if (!info) return null;
                
                // Get actual duration for tooltip
                const actualDurationFormatted = actualDuration ? formatDuration(actualDuration) : '-';
                
                return (
                  <span 
                    style={{ fontSize: '0.85em', marginLeft: '6px', color: info.color, cursor: 'help' }}
                    title={`Actual: ${actualDurationFormatted}`}
                  >
                    {info.text}
                  </span>
                );
              })()}
            </td>
            <td>
              {(() => {
                const isCompleted = item.lsch_status === 40;
                const hasActualRoute = isCompleted && item.lflight_dep_iata && item.lflight_arr_iata;
                const routeMismatch = hasActualRoute && 
                  (item.dep_iata !== item.lflight_dep_iata || item.arr_iata !== item.lflight_arr_iata);
                const planned = item.apair_distance;
                const actualRouteDistance = item.lflight_apair_distance;
                const actual = item.lflight_distance || actualRouteDistance;
                
                // If route changed, show both planned and actual route distances
                if (routeMismatch && actualRouteDistance != null) {
                  return (
                    <div>
                      <div style={{ textDecoration: 'line-through', color: '#7f8c8d' }}>
                        {formatDistance(planned)}
                      </div>
                      <div>
                        {formatDistance(actual || actualRouteDistance)}
                      </div>
                    </div>
                  );
                }
                
                // Normal display with delta
                const deltaMeters = isCompleted && actual != null && planned != null ? actual - planned : null;
                const hasDelta = deltaMeters !== null && deltaMeters !== undefined && deltaMeters !== 0;
                const deltaKm = hasDelta ? Math.round((deltaMeters / 1000) * 10) / 10 : null;
                const info = hasDelta ? formatDeltaValue(deltaKm, { positiveIsGood: true }) : null;
                
                return (
                  <>
                    {formatDistance(planned)}
                    {info && (
                      <span style={{ fontSize: '0.85em', marginLeft: '6px', color: info.color }}>
                        {info.text}
                      </span>
                    )}
                  </>
                );
              })()}
            </td>
            <td>
              {item.plane_reg || item.plane_msn || '-'}
            </td>
            <td>
              <span className={`badge ${legsScheduleStatusColors[item.lsch_status]}`}>
                {legsScheduleStatuses[item.lsch_status]}
              </span>
            </td>
          </>
        )}
      />
    );
}

export default LegsScheduleList;
